import React, { useState, useEffect, createContext, useContext } from 'react';
import { X } from 'lucide-react';
import { Link } from 'react-router-dom';

// Create a context to manage cookie consent visibility
export const CookieConsentContext = createContext({
  showCookieSettings: () => {},
});

export const useCookieConsent = () => useContext(CookieConsentContext);

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAcceptedCookies = localStorage.getItem('cookiesAccepted');
    
    if (!hasAcceptedCookies) {
      // Show cookie banner after a short delay
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for changes to localStorage to update visibility state
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cookiesAccepted') {
        setIsVisible(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  const acceptCookies = () => {
    localStorage.setItem('cookiesAccepted', 'true');
    setIsVisible(false);
  };

  const showCookieSettings = () => {
    setIsVisible(true);
  };
  
  if (!isVisible) {
    return (
      <CookieConsentContext.Provider value={{ showCookieSettings }}>
        {null}
      </CookieConsentContext.Provider>
    );
  }
  
  return (
    <CookieConsentContext.Provider value={{ showCookieSettings }}>
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
        <div className="max-w-4xl mx-auto glassmorphic p-4 md:p-6 rounded-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex-1">
            <h3 className="text-white text-lg font-semibold mb-2">Cookie Consent</h3>
            <p className="text-gray-300 text-sm">
              This website uses cookies to enhance your browsing experience. By continuing to use this site, 
              you consent to our use of cookies in accordance with our{' '}
              <Link to="/privacy" className="text-drummer-neon hover:underline">Privacy Policy</Link>.
            </p>
          </div>
          <div className="flex flex-shrink-0 gap-3 items-center">
            <button 
              onClick={() => setIsVisible(false)}
              className="px-4 py-2 text-sm font-medium bg-client-black/60 hover:bg-client-black/80 border border-client-gray/30 hover:border-client-yellow text-white hover:text-client-yellow rounded-md transition-all duration-300"
            >
              Decline
            </button>
            <button 
              onClick={acceptCookies}
              className="px-4 py-2 text-sm font-medium transition-all duration-300 bg-gradient-to-r from-client-yellow to-client-teal hover:from-client-teal hover:to-client-yellow text-client-black rounded-md"
            >
              Accept All
            </button>
            <button 
              onClick={() => setIsVisible(false)}
              className="text-white hover:text-gray-300 p-1"
              aria-label="Close cookie consent"
            >
              <X size={18} />
            </button>
          </div>
        </div>
      </div>
    </CookieConsentContext.Provider>
  );
};

export default CookieConsent;
